.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  margin: 0;
  padding: var(--space-16);
  list-style: none;
}

.user {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-4);
  padding-left: var(--space-40);
}

.user[data-animate] {
  animation: rise 240ms var(--ease-out) both;
}

.bubble {
  margin: 0;
  padding: var(--space-8) var(--space-12) var(--space-8) 14px;
  border-radius: var(--w-radius-bubble) var(--w-radius-bubble) 6px var(--w-radius-bubble);
  background: var(--w-accent);
  color: var(--w-on-accent);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-wrap: pretty;
}

.bubble[data-failed] {
  opacity: 0.7;
}

.failure {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-4) var(--space-8);
  margin: 0;
  color: var(--w-danger);
  font-size: 13px;
  text-align: right;
}

.failure > :global(.icon) {
  width: 16px;
  height: 16px;
}

.retry {
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  min-height: 32px;
  padding: 0 var(--space-8);
  border: 0;
  border-radius: 8px;
  background: var(--w-danger-bg);
  color: var(--w-danger);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.retry :global(.icon) {
  width: 14px;
  height: 14px;
}

.assistant {
  min-width: 0;
  padding-right: var(--space-8);
}

.prose {
  overflow-wrap: anywhere;
  text-wrap: pretty;
}

.prose > :first-child {
  margin-top: 0;
}

.prose > :last-child {
  margin-bottom: 0;
}

.prose p,
.prose ul,
.prose ol,
.prose table,
.prose blockquote {
  margin: 0 0 var(--space-8);
}

.prose ul,
.prose ol {
  padding-left: 1.25em;
}

.prose li + li {
  margin-top: var(--space-2);
}

.prose strong {
  font-weight: 650;
}

/* A link the widget refused (unknown host) reads as plain text. */
.prose a:not([href]) {
  color: inherit;
  font-weight: inherit;
  text-decoration: none;
}

.prose a[href] {
  color: var(--w-accent-text);
  font-weight: 550;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.prose h3,
.prose h4 {
  margin: var(--space-12) 0 var(--space-4);
  font-size: 15px;
  font-weight: 650;
}

.prose table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.prose th,
.prose td {
  padding: var(--space-4) var(--space-8);
  border-bottom: 1px solid var(--w-border);
  text-align: left;
  white-space: nowrap;
}

.prose th {
  color: var(--w-muted);
  font-weight: 600;
}

.prose code {
  padding: 0 4px;
  border-radius: 4px;
  background: var(--w-subtle);
  font-size: 0.9em;
}

.prose blockquote {
  padding-left: var(--space-12);
  border-left: 2px solid var(--w-border-strong);
  color: var(--w-muted);
}

.note {
  margin: var(--space-8) 0 0;
  color: var(--w-danger);
  font-size: 13px;
}

.waiting {
  display: inline-flex;
  align-items: center;
  gap: var(--space-8);
  padding: var(--space-8) 0;
}

.typing {
  display: inline-flex;
  gap: 4px;
}

/* Most answers start within a second: the words show only when the wait is longer. */
.waitingText {
  color: var(--w-muted);
  font-size: 13px;
  animation: appear 240ms var(--ease-out) 700ms both;
}

@keyframes appear {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
}

.typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--w-faint);
  animation: blink 1.2s ease-in-out infinite;
}

.typing span:nth-child(2) {
  animation-delay: 0.15s;
}

.typing span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes blink {
  0%,
  60%,
  100% {
    opacity: 0.3;
  }

  30% {
    opacity: 1;
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .user[data-animate] {
    animation: none;
  }

  .typing span {
    animation-duration: 2.4s;
  }

  .waitingText {
    animation-name: fade;
  }
}

@keyframes fade {
  from {
    opacity: 0;
  }
}

/* ---------- thumbs under an answer ---------- */
.rating {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-6) 0 0 -10px;
}

.rate {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--w-faint);
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 120ms var(--ease-out), background-color 120ms ease, color 120ms ease;
}

.rate :global(.icon) {
  width: 18px;
  height: 18px;
}

.rate[aria-pressed="true"] {
  color: var(--w-accent-text);
}

.rate:active {
  transform: scale(0.9);
  background: var(--w-press);
}

.rate:focus-visible {
  outline: 2px solid var(--w-accent-text);
  outline-offset: -2px;
}

@media (hover: hover) and (pointer: fine) {
  .rate:hover {
    background: var(--w-hover);
    color: var(--w-text);
  }

  .rate[aria-pressed="true"]:hover {
    color: var(--w-accent-text);
  }
}

.thanks {
  margin-left: var(--space-4);
  color: var(--w-muted);
  font-size: 12px;
  animation: fade 200ms ease both;
}

/* ---------- next steps under the last answer ---------- */
.followUps {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8);
  margin-top: var(--space-12);
}

.followUp {
  min-height: 40px;
  max-width: 100%;
  padding: var(--space-8) 14px;
  border: 1px solid var(--w-border-strong);
  border-radius: 999px;
  background: var(--w-bg);
  color: var(--w-text);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 120ms var(--ease-out), background-color 120ms ease, border-color 120ms ease, opacity 200ms ease;
  transition-delay: 0ms, 0ms, 0ms, calc(var(--i, 0) * 40ms);
  @starting-style {
    opacity: 0;
  }
}

/* The one that leads to a person (the contact form) carries the accent. */
.followUp[data-lead] {
  border-color: var(--w-accent-text);
  color: var(--w-accent-text);
}

.followUp:active {
  transform: scale(0.97);
  background: var(--w-press);
}

.followUp:focus-visible {
  outline: 2px solid var(--w-accent-text);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .followUp:hover {
    border-color: var(--w-accent-text);
    background: var(--w-hover);
  }
}
